import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext';
import {
  globalSearch,
  ENTITY_CONFIGS,
  type SearchEntityType,
  type GlobalSearchResult,
} from '../services/globalSearchService';
import { searchMissionHref } from '../lib/bureau/newMission';

/** The account a search row carries (contacts and opportunities select it), by its key. */
function readAccountId(raw: unknown): string | null {
  if (typeof raw !== 'object' || raw === null || !('account_id' in raw)) return null;
  const value = (raw as { account_id: unknown }).account_id;
  return typeof value === 'string' ? value : null;
}

export const useGlobalSearch = () => {
  const navigate = useNavigate();
  const sidebar = useSidebar();

  // Modal state
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Search state
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Map<SearchEntityType, GlobalSearchResult[]>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Keyboard navigation
  const [activeIndex, setActiveIndex] = useState(0);
  // Whether the user moved the selection (↑ ↓) since the last query: only then is a result picked
  // for « en faire une mission » — the first result, highlighted by default, is not a choice.
  const chose = useRef(false);

  // Refs for debounce & stale request cancellation
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const requestIdRef = useRef(0);

  // Flatten results for keyboard navigation
  const flatResults = useMemo(() => {
    const flat: GlobalSearchResult[] = [];
    for (const [, items] of results) {
      flat.push(...items);
    }
    return flat;
  }, [results]);

  // Ctrl+K / Escape keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setIsSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Listen for the global-search:open event (any component can open the search)
  useEffect(() => {
    const handleOpen = () => setIsSearchOpen(true);
    window.addEventListener('global-search:open', handleOpen);
    return () => window.removeEventListener('global-search:open', handleOpen);
  }, []);

  // Clear state when modal opens/closes
  useEffect(() => {
    if (isSearchOpen) {
      setQuery('');
      setResults(new Map());
      setTotalCount(0);
      setActiveIndex(0);
    }
  }, [isSearchOpen]);

  // Debounced search
  useEffect(() => {
    chose.current = false;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query || query.trim().length < 2) {
      setResults(new Map());
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const currentRequestId = ++requestIdRef.current;

    debounceRef.current = setTimeout(async () => {
      try {
        const response = await globalSearch(query);
        // Only update if this is still the latest request
        if (requestIdRef.current === currentRequestId) {
          setResults(response.results);
          setTotalCount(response.totalCount);
          setActiveIndex(0);
        }
      } catch (err) {
        console.error('Global search error:', err);
        if (requestIdRef.current === currentRequestId) {
          setResults(new Map());
          setTotalCount(0);
        }
      } finally {
        if (requestIdRef.current === currentRequestId) {
          setIsLoading(false);
        }
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  // Handle result selection (sidebar or navigate)
  const handleResultSelect = useCallback((result: GlobalSearchResult) => {
    const config = ENTITY_CONFIGS.find(c => c.type === result.entityType);
    if (!config) return;

    setIsSearchOpen(false);

    const action = config.action;
    if (action.type === 'sidebar') {
      sidebar[action.method](result.id);
    } else if (action.type === 'navigate') {
      navigate(action.getPath(result.raw));
    } else {
      void action.resolvePath(result.raw).then((path) => {
        if (path) navigate(path);
      });
    }
  }, [sidebar, navigate]);

  // Keyboard navigation handler (called from the modal)
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      chose.current = true;
      setActiveIndex(prev => (prev + 1) % Math.max(flatResults.length, 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      chose.current = true;
      setActiveIndex(prev => (prev - 1 + Math.max(flatResults.length, 1)) % Math.max(flatResults.length, 1));
    } else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      // « En faire une mission » (Encre): the picked object or the words typed go to « Nouvelle mission ».
      event.preventDefault();
      const picked = chose.current ? flatResults[activeIndex] ?? null : null;
      setIsSearchOpen(false);
      navigate(
        searchMissionHref(
          query,
          picked
            ? { kind: picked.entityType, id: picked.id, title: picked.title, accountId: readAccountId(picked.raw) }
            : null,
        ),
      );
    } else if (event.key === 'Enter' && flatResults.length > 0) {
      event.preventDefault();
      handleResultSelect(flatResults[activeIndex]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsSearchOpen(false);
    }
  }, [flatResults, activeIndex, handleResultSelect, navigate, query]);

  return {
    // Modal
    isSearchOpen,
    openSearch: () => setIsSearchOpen(true),
    closeSearch: () => setIsSearchOpen(false),
    // Search
    query,
    setQuery,
    results,
    isLoading,
    totalCount,
    // Keyboard nav
    activeIndex,
    setActiveIndex,
    flatResults,
    handleKeyDown,
    // Actions
    handleResultSelect,
  };
};
